/**
 * The system environment (style guide §3.1-§3.3, §3.7, §9.6; spec §4.4, §8): the baked seeded sky as background
 * and PMREM reflections, the live star layer, the star and gas-giant impostors, the key/fill/rim light rig from
 * environment.json, key-light shadows as a clipmap of light-space levels around the player (shadows.ts: High 4 ×
 * 2048², Medium 3 × 1024², Low 2 × 1024²) and the gate landmark (A9). Asset-lane owned (task A8 owns this file).
 * `update` and `setShadowFocus` are allocation-free.
 * M3 (R3, additive): `setSystemLook` re-seeds and re-bakes the sky for a Reach system, tints and scales the key by
 * the star class and toggles the gate and the impostors; `setSunDirection` turns the whole rig toward the star every
 * frame (allocation-free). The god rays read the key direction (atmosphere.ts), never the impostor, so they keep
 * tracking the star while the impostors are hidden. The sandbox calls neither and renders exactly as before.
 */
import {
  DirectionalLight,
  Group,
  PCFShadowMap,
  Quaternion,
  Vector3,
  type LightProbe,
  type Node,
  type Object3D,
  type Scene,
  type WebGPURenderer,
} from 'three/webgpu'
import { logger } from '../../core/logger'
import type { QualityPreset, TuningGroup } from '../contracts'
import { createGasGiant, createStar } from './bodies'
import { atmosphereOf, dustPatchLayout, dustTintFor, placeDustPatches, type AtmosphereState } from './atmosphere'
import { createFillProbe, stepFillProbe } from './fillProbe'
import { setCasterLodFromView } from '../viewLod'
import { loadEnvironmentData, shadowLevelsFor, shadowMapSizeFor, unitDirection, type EnvironmentFile } from './data'
import { createGate } from './gate'
import { createShadowFit, fitShadowFocus, type ShadowFit } from './shadowFit'
import { configureShadowLevels, createShadowLevels, disposeShadowLevels, outerShadowReach, shadowLevelBox, stepShadowLevels, type ShadowBox, type ShadowLevels, type ShadowLevelsConfig } from './shadows'
import { createSky } from './sky'
import { aimRig, createRigAim, keyColorFor, keyIntensityFor, nebulaForLook, type RigAim, type SystemLook } from './systemLook'
import { webgl2CapsOf } from '../webgl2'

export type { SystemLook, SystemRegion } from './systemLook'

export interface EnvironmentConfig {
  seed: number
}

export interface Environment {
  /** Sky, lights and impostors, centred on the camera (the render origin). Already added to the scene. */
  readonly root: Object3D
  /** The star's key light: the only shadow caster. */
  readonly sun: DirectionalLight
  /** Camera far plane (metres) the environment needs; gameplay sets camera.far to at least this. */
  readonly far: number
  readonly tuning: TuningGroup
  update(dt: number): void
  /** Fits the key light's shadow map around the player ship. Render space only (D-011). */
  setShadowFocus(centerRenderSpace: Vector3, radius: number): void
  /**
   * PF-2 (read-only): the distance from the render origin within which a caster can still touch the outer active
   * shadow level (the level box's corner distance from the focus plus the focus's distance from the origin).
   * Infinity when no level is active. Allocation-free.
   */
  shadowReach(): number
  /**
   * PF-2b (read-only): writes shadow level `level`'s last fitted box (render space; clamped to the outer active level)
   * into `out`. False when no level is active. Allocation-free.
   */
  shadowBox(level: number, out: ShadowBox): boolean
  /**
   * Additive (lighting overhaul): the dust haze volume of the current asteroid field, render space, every frame
   * the field moves (radius 0 = no dust). Allocation-free.
   */
  setDustVolume(centerRenderSpace: Vector3, radius: number): void
  /**
   * Additive (M2, M2-20): the tetherstone foreshadowing flicker of the gate ring (D-031, never explained): a quick
   * pattern of emissive flares and dips over about 1.5 s at `strength` 0..1, decaying to dark. Allocation-free.
   */
  setGateFlicker(strength: number): void
  setPreset(preset: QualityPreset): void
  /**
   * Additive (M3 R3): becomes a Reach system's environment. Region nebula with seeded variation, a re-seeded star
   * catalogue, both cubes and the PMREM re-baked; key colour and intensity from the star; gate and impostor
   * toggles. Resolves after GPU idle with the measured bake time. Never called by the sandbox.
   */
  setSystemLook(look: SystemLook): Promise<{ bakeMs: number }>
  /**
   * Additive (M3 R3): the unit vector toward the star (render space). Turns the key light, and fill and rim by the
   * same rotation, then refits the shadows and the atmosphere. Allocation-free (called every frame in the Reach).
   */
  setSunDirection(x: number, y: number, z: number): void
  /** Additive (M3 R3): the baked background node (`scene.backgroundNode`), stable across re-bakes (R8 draws it). */
  readonly skyBackground: Node<'vec4'>
  dispose(): void
}

/** Distance (metres) of the fill and rim lights from the origin: directional, so only the direction matters. */
export const RIG_DISTANCE = 100

// ---------------------------------------------------------------------------------------------------------------
// Shadow focus maths (render space, tested)
// ---------------------------------------------------------------------------------------------------------------

export { createShadowFit, fitShadowFocus, type ShadowFit } from './shadowFit'

// ---------------------------------------------------------------------------------------------------------------
// Per-frame state (tested for allocation-freedom)
// ---------------------------------------------------------------------------------------------------------------

export interface FrameState {
  readonly sun: DirectionalLight
  /** Shadow focus centre in render space and radius (set by setShadowFocus). */
  readonly focus: Vector3
  radius: number
  mapSize: number
  margin: number
  readonly fit: ShadowFit
  /** The key light's shadow clipmap (null in tests that only exercise the rig). */
  readonly levels: ShadowLevels | null
  /** Shared with the post graph: sun direction and colour (atmosphere.ts; null in rig-only tests). */
  readonly atmos: AtmosphereState | null
  /** Fill and rim holders projected into the SH fill probe every frame (fillProbe.ts; null in rig-only tests). */
  readonly fillProbe: { readonly probe: LightProbe; readonly fill: DirectionalLight; readonly rim: DirectionalLight } | null
  /** Gas-giant band spin (radians, wrapped) and its speed (rad/s). */
  readonly spin: { value: number }
  flowSpeed: number
}

const TAU = Math.PI * 2

/** Advances the band flow and refits the key light's shadow camera. Allocation-free. */
export function stepEnvironment(s: FrameState, dt: number): void {
  s.spin.value = (s.spin.value + s.flowSpeed * dt) % TAU
  const sun = s.sun
  const t = sun.target.position
  const p = sun.position
  fitShadowFocus(p.x - t.x, p.y - t.y, p.z - t.z, s.focus.x, s.focus.y, s.focus.z, s.radius, s.mapSize, s.margin, s.fit)
  t.set(s.fit.tx, s.fit.ty, s.fit.tz)
  p.set(s.fit.px, s.fit.py, s.fit.pz)
  const cam = sun.shadow.camera
  if (cam.right !== s.fit.half || cam.far !== s.fit.far) {
    cam.left = -s.fit.half
    cam.right = s.fit.half
    cam.bottom = -s.fit.half
    cam.top = s.fit.half
    cam.near = s.fit.near
    cam.far = s.fit.far
    cam.updateProjectionMatrix()
  }
  if (s.atmos !== null) {
    const lx = p.x - t.x
    const ly = p.y - t.y
    const lz = p.z - t.z
    const l = Math.sqrt(lx * lx + ly * ly + lz * lz)
    if (l > 0) s.atmos.sunDirection.set(lx / l, ly / l, lz / l)
    s.atmos.sunColor.copy(sun.color).multiplyScalar(sun.intensity)
  }
  if (s.fillProbe !== null) stepFillProbe(s.fillProbe.probe, s.fillProbe.fill, s.fillProbe.rim)
  if (s.levels !== null) stepShadowLevels(s.levels, p.x - t.x, p.y - t.y, p.z - t.z, s.focus.x, s.focus.y, s.focus.z, s.radius)
}

// ---------------------------------------------------------------------------------------------------------------
// Rig aim: impostors follow the rig (M3 R3)
// ---------------------------------------------------------------------------------------------------------------

/** The impostors and their authored poses; `followRig` rotates them rigidly with the rig. */
export interface ImpostorFollow {
  readonly star: Object3D
  readonly starPos0: Vector3
  readonly starQuat0: Quaternion
  readonly giant: Object3D
  readonly giantPos0: Vector3
  readonly giantQuat0: Quaternion
}

/** Places the star and the giant at their authored poses turned by the rig's rotation. Allocation-free. */
export function followRig(f: ImpostorFollow, rig: RigAim): void {
  f.star.position.copy(f.starPos0).applyQuaternion(rig.rotation)
  f.star.quaternion.copy(rig.rotation).multiply(f.starQuat0)
  f.giant.position.copy(f.giantPos0).applyQuaternion(rig.rotation)
  f.giant.quaternion.copy(rig.rotation).multiply(f.giantQuat0)
}

// ---------------------------------------------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------------------------------------------

/** A rig light named for lookdev (sun, fill, rim) at RIG_DISTANCE along its normalized direction. */
export function createRigLight(name: string, l: EnvironmentFile['lights']['key']): DirectionalLight {
  const light = new DirectionalLight(l.color, l.intensity)
  light.name = name
  const d = unitDirection(l.direction)
  light.position.set(d[0] * RIG_DISTANCE, d[1] * RIG_DISTANCE, d[2] * RIG_DISTANCE)
  return light
}

function applyShadowPreset(env: EnvironmentFile, preset: QualityPreset, state: FrameState): void {
  const size = shadowMapSizeFor(env, preset)
  state.mapSize = size
  if (state.levels) configureShadowLevels(state.levels, shadowLevelsFor(env, preset), size)
  setCasterLodFromView(env.shadows.casterLodFromView[preset])
}

/**
 * The clipmap configuration from environment.json (mutable copy: the dev panel tunes the bias). `levelCap` limits
 * the allocated levels (the WebGL2 fallback, D-053); presets then activate at most that many.
 */
export function shadowLevelsConfig(env: EnvironmentFile, levelCap = Infinity): ShadowLevelsConfig & { normalBiasTexels: number; biasTexels: number } {
  const s = env.shadows
  return {
    maxLevels: Math.min(Math.max(s.levels.low, s.levels.medium, s.levels.high), levelCap),
    ratio: s.ratio,
    focusScale: s.focusScale,
    minHalfExtent: s.minHalfExtent,
    fade: s.fade,
    casterMargin: s.casterMargin,
    rockCasterMargin: s.rockCasterMargin,
    skipZeroLevels: s.skipZeroLevels,
    normalBiasTexels: s.normalBiasTexels,
    biasTexels: s.biasTexels,
  }
}

export async function createEnvironment(
  renderer: WebGPURenderer,
  scene: Scene,
  cfg: EnvironmentConfig,
  preset: QualityPreset,
): Promise<Environment> {
  const env = loadEnvironmentData()
  renderer.toneMappingExposure = env.exposure
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = PCFShadowMap

  const root = new Group()
  root.name = 'environment'

  // Sky: baked background + PMREM reflections (replaces the M0 ambient stand-in, style guide §3.3).
  const sky = await createSky(renderer, env, cfg.seed, preset)
  const bgScene = scene as Scene & { backgroundNode?: unknown }
  bgScene.backgroundNode = sky.backgroundNode
  scene.environment = sky.environment
  scene.environmentIntensity = env.environmentIntensity
  root.add(sky.stars)
  logger.info(`Environment: sky baked (${sky.stats.bakeSize}² background + ${env.sky.envSize}² PMREM) in ${sky.stats.bakeMs.toFixed(1)} ms`)

  // Atmosphere shared with the post graph (fog colour from the baked sky, dust and fog settings).
  const atmos = atmosphereOf(scene)
  const a = env.atmosphere
  atmos.sky = sky.environment
  atmos.fog.set(a.fog.start, a.fog.density, a.fog.max, a.fog.roughness)
  atmos.dustParams.set(a.dust.opticalDepth, a.dust.anisotropy, a.dust.albedo, a.dust.maxOpacity)
  atmos.tint.set(a.dust.ambient, a.fog.gain, a.dust.isotropic, 0)
  atmos.dustShape.set(a.dust.wisp.scale, a.dust.wisp.contrast, 0, 0)
  // Dust as a few small pockets per field (not one veil over the whole field), tinted by the system's nebula.
  const dustLayout = dustPatchLayout(a.dust.patches)
  dustTintFor(env.sky.nebula.hues, a.dust.tint, atmos.dustTint)

  // Light rig.
  const sun = createRigLight('sun', env.lights.key)
  // WebGL2 builds a lean clipmap (webgl2.json, D-053): fewer levels and one hardware PCF tap.
  const caps = webgl2CapsOf(renderer)
  const levelCfg = shadowLevelsConfig(env, caps?.shadowLevels)
  const levels = createShadowLevels(sun, levelCfg, env.shadows.intensity, caps?.shadowFilter)
  // Fill and rim are area sources: invisible holders projected into an SH probe (indirect light, AO-occluded).
  const fill = createRigLight('fill', env.lights.fill)
  const rim = createRigLight('rim', env.lights.rim)
  fill.visible = false
  rim.visible = false
  const probe = createFillProbe()
  root.add(sun, sun.target, fill, rim, probe)

  const star = createStar(env)
  const giant = createGasGiant(env, cfg.seed)
  root.add(star.root, giant.root)
  const gate = createGate()
  root.add(gate.root)
  scene.add(root)

  // Rig aim (M3): the impostors follow the rig's rotation from its authored pose, so they stay on the key and fill.
  const rig = createRigAim(sun, fill, rim, env, RIG_DISTANCE)
  const follow: ImpostorFollow = {
    star: star.root,
    starPos0: star.root.position.clone(),
    starQuat0: star.root.quaternion.clone(),
    giant: giant.root,
    giantPos0: giant.root.position.clone(),
    giantQuat0: giant.root.quaternion.clone(),
  }

  const state: FrameState = {
    sun,
    focus: new Vector3(0, 0, 0),
    radius: env.shadows.defaultRadius,
    mapSize: 0,
    margin: env.shadows.casterMargin,
    fit: createShadowFit(),
    levels,
    fillProbe: { probe, fill, rim },
    atmos,
    spin: giant.uniforms.spin,
    flowSpeed: giant.flowSpeed,
  }
  applyShadowPreset(env, preset, state)
  stepEnvironment(state, 0)
  root.userData.environmentStats = sky.stats

  const nebula = { ...env.sky.nebula }
  const tuning: TuningGroup = {
    name: 'Environment',
    params: {
      exposure: { value: env.exposure, min: 0.5, max: 2, step: 0.01 },
      environmentIntensity: { value: env.environmentIntensity, min: 0, max: 1.5, step: 0.01 },
      keyIntensity: { value: env.lights.key.intensity, min: 0, max: 8, step: 0.05 },
      fillIntensity: { value: env.lights.fill.intensity, min: 0, max: 3, step: 0.05 },
      rimIntensity: { value: env.lights.rim.intensity, min: 0, max: 3, step: 0.05 },
      starIntensity: { value: env.star.intensity, min: 50, max: 200, step: 1 },
      starGlare: { value: env.star.glare.intensity, min: 0, max: 6, step: 0.05 },
      starStreak: { value: env.star.streak.intensity, min: 0, max: 4, step: 0.05 },
      skyStars: { value: 1, min: 0, max: 3, step: 0.05 },
      atmosphere: { value: env.gasGiant.atmosphere.intensity, min: 0, max: 4, step: 0.05 },
      nebulaBrightness: { value: nebula.brightness, min: 0.05, max: 2, step: 0.01 },
      nebulaWarp: { value: nebula.warp, min: 0, max: 3, step: 0.01 },
      nebulaDust: { value: nebula.dust, min: 0, max: 1, step: 0.01 },
      nebulaBandGlow: { value: nebula.bandGlow, min: 0, max: 2, step: 0.01 },
      shadowIntensity: { value: env.shadows.intensity, min: 0, max: 1, step: 0.01 },
      fogDensity: { value: a.fog.density, min: 0, max: 0.0005, step: 0.000005 },
      fogMax: { value: a.fog.max, min: 0, max: 1, step: 0.01 },
      dustDepth: { value: a.dust.opticalDepth, min: 0, max: 2, step: 0.01 },
      dustAlbedo: { value: a.dust.albedo, min: 0, max: 1, step: 0.01 },
      shadowBiasTexels: { value: env.shadows.biasTexels, min: 0, max: 8, step: 0.1 },
      shadowNormalBiasTexels: { value: env.shadows.normalBiasTexels, min: 0, max: 8, step: 0.1 },
    },
    apply(): void {
      const n = (k: string): number => {
        const v = tuning.params[k]?.value
        return typeof v === 'number' ? v : 0
      }
      renderer.toneMappingExposure = n('exposure')
      scene.environmentIntensity = n('environmentIntensity')
      sun.intensity = n('keyIntensity')
      fill.intensity = n('fillIntensity')
      rim.intensity = n('rimIntensity')
      star.uniforms.intensity.value = n('starIntensity')
      star.uniforms.glare.value = n('starGlare')
      star.uniforms.streak.value = n('starStreak')
      sky.starBrightness.value = n('skyStars')
      giant.uniforms.atmosphere.value = n('atmosphere')
      levels.intensity.value = n('shadowIntensity')
      atmos.fog.y = n('fogDensity')
      atmos.fog.z = n('fogMax')
      atmos.dustParams.x = n('dustDepth')
      atmos.dustParams.z = n('dustAlbedo')
      levelCfg.biasTexels = n('shadowBiasTexels')
      levelCfg.normalBiasTexels = n('shadowNormalBiasTexels')
      stepEnvironment(state, 0)
      const changed =
        nebula.brightness !== n('nebulaBrightness') || nebula.warp !== n('nebulaWarp') || nebula.dust !== n('nebulaDust') || nebula.bandGlow !== n('nebulaBandGlow')
      if (changed) {
        nebula.brightness = n('nebulaBrightness')
        nebula.warp = n('nebulaWarp')
        nebula.dust = n('nebulaDust')
        nebula.bandGlow = n('nebulaBandGlow')
        sky.rebake(nebula)
      }
    },
  }

  // setSystemLook writes the tuning values it changes (the nebula copy first, so apply() starts no second bake).
  const setParam = (k: string, v: number): void => {
    const param = tuning.params[k]
    if (param) param.value = v
  }

  return {
    root,
    sun,
    far: env.sky.radius * 2,
    tuning,
    skyBackground: sky.backgroundNode,
    async setSystemLook(look: SystemLook): Promise<{ bakeMs: number }> {
      const cfg = nebulaForLook(env, look.region, look.seed)
      Object.assign(nebula, cfg)
      dustTintFor(cfg.hues, a.dust.tint, atmos.dustTint)
      const key = keyIntensityFor(env, look.luminosity)
      keyColorFor(env, look.starColor, sun.color)
      setParam('nebulaBrightness', cfg.brightness)
      setParam('nebulaWarp', cfg.warp)
      setParam('nebulaDust', cfg.dust)
      setParam('nebulaBandGlow', cfg.bandGlow)
      setParam('keyIntensity', key)
      tuning.apply()
      gate.root.visible = look.showGate
      star.root.visible = look.showImpostors
      giant.root.visible = look.showImpostors
      const bakeMs = await sky.reseed(look.seed, nebula)
      logger.info(`Environment: system look (seed ${look.seed}, ${look.region}) re-baked in ${bakeMs.toFixed(1)} ms`)
      return { bakeMs }
    },
    setSunDirection(x: number, y: number, z: number): void {
      aimRig(rig, x, y, z)
      followRig(follow, rig)
      stepEnvironment(state, 0)
    },
    update(dt: number): void {
      stepEnvironment(state, dt)
      gate.update(dt)
    },
    setShadowFocus(centerRenderSpace: Vector3, radius: number): void {
      state.focus.copy(centerRenderSpace)
      state.radius = radius > 0 ? radius : env.shadows.defaultRadius
      stepEnvironment(state, 0)
    },
    shadowReach(): number {
      return state.levels === null ? Number.POSITIVE_INFINITY : outerShadowReach(state.levels, state.radius) + state.focus.length()
    },
    shadowBox(level: number, out: ShadowBox): boolean {
      return state.levels !== null && shadowLevelBox(state.levels, level, out)
    },
    setDustVolume(centerRenderSpace: Vector3, radius: number): void {
      atmos.dust.set(centerRenderSpace.x, centerRenderSpace.y, centerRenderSpace.z, Math.max(radius, 0))
      placeDustPatches(atmos, dustLayout, centerRenderSpace.x, centerRenderSpace.y, centerRenderSpace.z, radius)
    },
    setGateFlicker(strength: number): void {
      gate.setFlicker(strength)
    },
    setPreset(p: QualityPreset): void {
      applyShadowPreset(env, p, state)
      sky.setPreset(p)
      stepEnvironment(state, 0)
    },
    dispose(): void {
      gate.dispose()
      star.dispose()
      giant.dispose()
      disposeShadowLevels(levels)
      sun.shadow.dispose()
      sky.dispose()
      if (bgScene.backgroundNode === sky.backgroundNode) bgScene.backgroundNode = null
      if (scene.environment === sky.environment) scene.environment = null
      if (atmos.sky === sky.environment) atmos.sky = null
      atmos.dust.w = 0
      placeDustPatches(atmos, dustLayout, 0, 0, 0, 0)
      root.removeFromParent()
    },
  }
}
